import type { ApiLockerKeyCurrentAssignment } from '@mfw/common';

/**
 * Http statuses supported by BE API
 */
export enum HttpStatus {
  UnprocessableEntity = 422,
  /**
   * Not a status any server sends: the request was sent and no response came back. The
   * same 0 `XMLHttpRequest.status` reports for an aborted or failed request.
   */
  NoResponse = 0,
  InternalServerError = 500,
  ContentTooLarge = 413,
  Unauthorized = 401,
  BadRequest = 400,
  Forbidden = 403,
  NotFound = 404,
  Conflict = 409,
  /** A throttle bucket refused the request (`_globals/throttling.ts` on the backend). */
  TooManyRequests = 429,
  Ok = 200,
}

/**
 * Response from backend containing data
 */
export type BaseApiResponse<TData> = {
  status: HttpStatus;
  message: string;
  data: TData;
};

/**
 * Response from API with next page, previous page and total number of elements
 */
export type BaseApiPaginatedResponse<TData> = BaseApiResponse<TData> & {
  total: number;
  /**
   * A content search that found nothing and fell back to typo/inflection
   * alternatives: the corrected words, `''` when none is safe to show
   * (generic notice). Absent = no correction. The list hook reads it from
   * the first page only.
   */
  correctedQuery?: string;
};

/**
 * Response from API when there is an error
 */
export type BaseApiErrorResponse<TErrors> = {
  errors: TErrors | null;
  status: HttpStatus;
  message: string;
  /** machine-readable BE error code (e.g. NO_SAVED_CARD) — FE branches on it, never on the translated message */
  code?: string;
  /**
   * Task 5 (membership-renewal plan) — carried only by
   * MEMBERSHIP_VISITS_USED_UP (check-in refused at the visit limit): the
   * exhausted row's own id and expiry, so the used-up-choices dialog can act
   * on it without a re-fetch. Absent on every other error.
   */
  userMembershipId?: string;
  endDate?: string;
  /**
   * Carried only by MEMBERSHIP_ALREADY_CHECKED_IN_TODAY: the membership's
   * existing check-in for today, so the duplicate-check-in dialog can show
   * it without a re-fetch. Absent on every other error.
   */
  existingCheckIn?: {
    id: string;
    checkInDate: string;
    author: { id: string; name: string } | null;
  };
  /**
   * Carried only by LOCKER_KEY_HOLDER_HAS_KEY (409): the holder's live key,
   * which the Issue dialog names and offers to Replace — its id is the
   * stale-confirmation guard the Replace request sends back.
   */
  currentAssignment?: ApiLockerKeyCurrentAssignment;
  /** Carried only by LOCKER_KEY_ALREADY_DISABLED (409): the open record. */
  disabledNumberId?: string;
};

export type BaseApiArrayResponse<TFieldName extends string> = {
  field: TFieldName;
  message: string;
  index: number;
};

export type FieldArrayErrorResponse = {
  message: string;
  index: number;
  field: string;
};
